import {objectFromEntries} from '@benchling/js-utils';

import {LayeringMode} from 'constants/MapsConstants';

export interface LayersDict {
  [layerIndex: number]: number;
}

/**
 * Whether feature wraps and starts/ends on the same slice
 * @param start The start residue index of the slice
 * @param end The end residue index of the slice
 * @param drawStart feature.drawStart(polymer). Usually just `start`, but for primers can be an out-of-bounds
 *   overhang value
 * @param drawEnd feature.drawEnd(polymer). Usually just `end`, but for primers can be an out-of-bounds
 *   overhang value
 */
export function badWraps(start: number, end: number, drawStart: number, drawEnd: number): boolean {
  return start < drawEnd && drawEnd <= drawStart && drawStart < end;
}

/**
 * Figures out how many times a feature will appear in a slice (i.e. whether it wraps around)
 * @param model A generic model for the region
 * @param start The start residue index of the slice
 * @param end The end residue index of the slice
 * @param drawStart feature.drawStart(polymer). Usually just `start`, but for primers can be an out-of-bounds
 *   overhang value
 * @param drawEnd feature.drawEnd(polymer). Usually just `end`, but for primers can be an out-of-bounds
 *   overhang value
 */
export function timesPerSlice<M>(
  model: M,
  start: number,
  end: number,
  drawStart: number,
  drawEnd: number
): Array<M> {
  if (badWraps(start, end, drawStart, drawEnd)) {
    // Index here gets fed in as i to whichDisplay (0 is ending fragment, 1 is starting fragment)
    return [model, model];
  } else {
    return [model];
  }
}

/**
 * Relevant for features that are rendered twice in a single slice because of circular sequences.
 * @param start The start residue index of the slice
 * @param end The end residue index of the slice
 * @param drawStart feature.drawStart(polymer). Usually just `start`, but for primers can be an out-of-bounds
 *   overhang value
 * @param drawEnd feature.drawEnd(polymer). Usually just `end`, but for primers can be an out-of-bounds
 *   overhang value
 * @param i The segment of the feature being drawn, i.e. if it wraps around
 * @param polymerLength The length of the underlying sequence
 */
export function whichDisplay(
  start: number,
  end: number,
  drawStart: number,
  drawEnd: number,
  i: number,
  polymerLength: number
): {start: number; end: number} | null {
  if (drawEnd === 0) {
    drawEnd = polymerLength;
  }

  const showLeft = start <= drawStart && drawStart < end;
  const showRight = (start <= drawEnd && drawEnd < end) || drawEnd === end;
  const badWrap = badWraps(start, end, drawStart, drawEnd);
  const showSecond = i === 1;

  if (showLeft && showRight && !badWrap) {
    if (showSecond) {
      return null;
    }
    return {start: drawStart, end: drawEnd};
  }

  if (showLeft && (!showRight || (badWrap && !showSecond))) {
    return {start: drawStart, end: end + 1};
  }

  if (showRight && (!showLeft || (badWrap && showSecond))) {
    return {start: start - 1, end: drawEnd};
  }

  return {start: start - 1, end: end + 1};
}

/**
 * Determines where the feature starts and ends, accounting for features that wrap around a line.
 * @param start The start residue index of the slice
 * @param end The end residue index of the slice
 * @param drawStart feature.drawStart(polymer). Usually just `start`, but for primers can be an out-of-bounds
 *   overhang value
 * @param drawEnd feature.drawEnd(polymer). Usually just `end`, but for primers can be an out-of-bounds
 *   overhang value
 * @param polymerLength The length of the underlying polymer
 */
export function span(
  start: number,
  end: number,
  drawStart: number,
  drawEnd: number,
  polymerLength: number
): {start: number; end: number} | null {
  if (badWraps(start, end, drawStart, drawEnd)) {
    // if the annotation wraps on the same line, it takes up an entire line.
    return {start: start - 1, end: end + 1};
  } else {
    return whichDisplay(start, end, drawStart, drawEnd, 0, polymerLength);
  }
}

export interface Layerable<TLayerMode extends LayeringMode> extends Backbone.Model {
  get(layeringMode: TLayerMode): number;
}

/**
 * Gets a map of layer indices on this slice to what feature index to draw at that layer.
 * @param models A list of RegionSelectable to draw on a given slice.
 * @param layeringMode How to layer features over each other.
 */
export function getLayersDict<TLayerMode extends LayeringMode>(
  models: Array<Layerable<TLayerMode>>,
  layeringMode: TLayerMode
): LayersDict {
  const dedupedLayerIndices = new Set(models.map((model) => model.get(layeringMode)));
  const orderedDedupedLayerIndices = Array.from(dedupedLayerIndices).sort((i, j) => i - j);
  return objectFromEntries(orderedDedupedLayerIndices.map((layerIndex, i) => [layerIndex, i]));
}
